import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { MemoryModel, formatLocation } from "common247";
import { useFirebase } from "@/components/firebase/useFirebase";
import { getMyMemories } from "@/services/firebase/memory-functions";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedToolTip } from "@/components/ThemedToolTip";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors } from "@/constants/ThemeColours";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the MemorySelector popup component.
 */
export interface IMemorySelectorProps {
  /** Whether the memory selector popup is currently visible */
  visible: boolean;
  /** Callback invoked when the user dismisses the selector */
  onClose: () => void;
  /** Number of selected items being assigned (single-select context) */
  selectedCount?: number;
  /** Label describing the type of items being assigned (e.g., "media", "contacts", "groups") */
  itemTypeLabel?: string;
  /** Callback fired when an existing memory is selected (single-select) */
  onSelectMemory?: (memory: MemoryModel) => Promise<void> | void;
  /** Whether multiple memories can be selected */
  multiSelect?: boolean;
  /** Initially or currently selected memory IDs */
  selectedMemoryIds?: string[];
  /** Callback fired when multiple memories are selected */
  onSelectMultipleMemories?: (memories: MemoryModel[]) => Promise<void> | void;
  /** Callback fired when the "+ Create new memory..." action is triggered */
  onCreateNewMemory?: () => void;
  /** Whether the "+ Create new memory..." option is allowed/rendered (default: true) */
  allowCreate?: boolean;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Optional pre-fetched memories list; if omitted, the component fetches automatically */
  memories?: MemoryModel[];
  /** Optional title override (default: "Memory Selector") */
  title?: string;
  /** Optional subtitle or instruction text */
  subtitle?: string;
  /** Target width for the popover card (default: 480) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID */
  testID?: string;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Compact hover/popup Memory Selector displaying a searchable list of memories
 * with an optional prominent "+ Create new memory..." option at the very top.
 * Supports both single-select and multi-select modes.
 * Complies with Memories UX Standards (square borders, ThemedToolTip, token colors).
 */
export function MemorySelector({
  visible,
  onClose,
  anchorPosition,
  selectedCount = 1,
  itemTypeLabel = "items",
  onSelectMemory,
  multiSelect = false,
  selectedMemoryIds = DEFAULT_EMPTY_IDS,
  onSelectMultipleMemories,
  onCreateNewMemory,
  allowCreate = true,
  memories: propMemories,
  title = "Memory Selector",
  subtitle: propSubtitle,
  width = 480,
  maxHeight = 520,
  testID = "memory-selector",
}: IMemorySelectorProps) {
  const services = useFirebase();
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedMemoryIds ?? []);
  const [internalMemories, setInternalMemories] = useState<MemoryModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredMemoryId, setHoveredMemoryId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedMemoryIds) {
      setSelectedIds((prev) => {
        if (
          prev.length === selectedMemoryIds.length &&
          prev.every((id, idx) => id === selectedMemoryIds[idx])
        ) {
          return prev;
        }
        return selectedMemoryIds;
      });
    }
  }, [selectedMemoryIds]);

  const { height } = useWindowDimensions();
  const maxDisplayCount = height < 640 ? 2 : 3;

  const { selector: selectorTheme, palette } = useComponentTheme();
  const borderColor = selectorTheme.row.restingBorderColor;
  const hoverBorderColor = selectorTheme.row.hoverBorderColor;
  const tintColor = selectorTheme.row.selectedBorderColor;
  const textMuted = selectorTheme.row.subtitleColor;
  const badgeBg = selectorTheme.row.restingBg;

  useEffect(() => {
    if (!visible) return;
    if (propMemories && propMemories.length > 0) {
      setInternalMemories(propMemories);
      return;
    }
    if (!services) return;

    let isMounted = true;
    setLoading(true);

    getMyMemories(services, {})
      .then((res) => {
        if (!isMounted) return;
        if (res.status && res.memories) {
          setInternalMemories(res.memories);
        } else {
          CustomAlert.alert("Error", res.error || "Failed to load memories");
        }
      })
      .catch((err: any) => {
        if (!isMounted) return;
        console.error("Error loading memories for MemorySelector:", err);
        CustomAlert.alert("Error", err.message || "Failed to load memories");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [visible, services, propMemories]);

  const activeList = propMemories && propMemories.length > 0 ? propMemories : internalMemories;

  const filteredMemories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return activeList;
    return activeList.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        (m.description && m.description.toLowerCase().includes(q)) ||
        (m.location && formatLocation(m.location).toLowerCase().includes(q))
    );
  }, [activeList, searchQuery]);

  const handleSelect = async (memory: MemoryModel) => {
    if (!memory.id) return;

    if (multiSelect) {
      const next = selectedIds.includes(memory.id)
        ? selectedIds.filter((id) => id !== memory.id)
        : [...selectedIds, memory.id];
      setSelectedIds(next);
      if (onSelectMultipleMemories) {
        const selectedList = activeList.filter((m) => next.includes(m.id!));
        await onSelectMultipleMemories(selectedList);
      }
      return;
    }

    if (submittingId) return;
    setSubmittingId(memory.id);
    try {
      if (onSelectMemory) {
        await onSelectMemory(memory);
      }
      onClose();
    } catch (err: any) {
      console.error("Error in onSelectMemory callback:", err);
      CustomAlert.alert("Error", err.message || "Failed to add items to memory");
    } finally {
      setSubmittingId(null);
    }
  };

  if (!visible) return null;

  const computedSubtitle =
    propSubtitle ??
    (multiSelect
      ? `${selectedIds.length} memor${selectedIds.length === 1 ? "y" : "ies"} selected`
      : `Choose a memory to receive ${selectedCount} selected ${itemTypeLabel}:`);

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={title}
      icon="bookmark-outline"
      subtitle={computedSubtitle}
      width={width}
      maxHeight={maxHeight}
      testID={testID}
      footer={
        <View style={styles.footer}>
          <ThemedButton
            title="Cancel"
            variant="secondary"
            onPress={onClose}
            style={styles.cancelBtn}
          />
          {multiSelect && (
            <ThemedButton
              title="Done"
              variant="primary"
              compact
              onPress={async () => {
                if (onSelectMultipleMemories) {
                  const selectedList = activeList.filter((m) => selectedIds.includes(m.id!));
                  await onSelectMultipleMemories(selectedList);
                }
                onClose();
              }}
              style={styles.doneBtn}
              testID={`${testID}-done-btn`}
            />
          )}
        </View>
      }
    >
      <View style={{ flex: 1 }}>

        {/* Search bar */}
        <View style={styles.searchWrapper}>
          <Ionicons name="search-outline" size={15} color={tintColor} style={styles.searchIcon} />
          <ThemedInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search memories..."
            style={[styles.searchInput, { borderColor }]}
            containerStyle={styles.searchContainer}
          />
          {searchQuery.length > 0 && (
            <ThemedToolTip text="Clear search text">
              <TouchableOpacity
                onPress={() => setSearchQuery("")}
                style={styles.clearSearchBtn}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              >
                <Ionicons name="close-circle" size={15} color={textMuted} />
              </TouchableOpacity>
            </ThemedToolTip>
          )}
        </View>

        {/* "+ Create new memory..." option placed at the TOP of the list */}
        {allowCreate && onCreateNewMemory ? (
          <ThemedToolTip text="Create a brand new memory with selected items">
            <TouchableOpacity
              style={[
                styles.createNewRow,
                {
                  backgroundColor: badgeBg,
                  borderColor: tintColor,
                },
              ]}
              onPress={() => {
                onClose();
                onCreateNewMemory();
              }}
              activeOpacity={0.7}
              accessibilityLabel="Create new memory"
            >
              <View style={[styles.createNewIconCircle, { backgroundColor: tintColor }]}>
                <Ionicons name="add" size={16} color="#FFFFFF" />
              </View>
              <View style={styles.createNewTextCol}>
                <ThemedText type="defaultSemiBold" style={[styles.createNewTitle, { color: tintColor }]}>
                  + Create new memory...
                </ThemedText>
                <ThemedText style={[styles.createNewSubtitle, { color: textMuted }]}>
                  Start a fresh archival record with {selectedCount} {itemTypeLabel}
                </ThemedText>
              </View>
              <Ionicons name="chevron-forward" size={16} color={tintColor} />
            </TouchableOpacity>
          </ThemedToolTip>
        ) : null}

        {/* Scrollable memory list */}
        <ScrollView
          style={styles.listContainer}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={true}
          persistentScrollbar={true}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
        >
          {loading ? (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="small" color={tintColor} />
              <ThemedText style={[styles.infoText, { color: textMuted, marginTop: 8 }]}>
                Loading available memories...
              </ThemedText>
            </View>
          ) : filteredMemories.length === 0 ? (
            <View style={styles.centerContainer}>
              <ThemedText style={[styles.infoText, { color: textMuted }]}>
                {activeList.length === 0
                  ? "No existing memories found."
                  : "No memories match your search."}
              </ThemedText>
            </View>
          ) : (
            filteredMemories.map((m) => {
              const isHovered = hoveredMemoryId === m.id;
              const isSubmitting = submittingId === m.id;
              const isSelected = selectedIds.includes(m.id!);
              const mediaCount = m.mediaIds?.length ?? m.media?.length ?? 0;
              const formattedDate = m.date
                ? new Date(m.date).toLocaleDateString()
                : m.createdAt
                ? new Date(m.createdAt).toLocaleDateString()
                : "";

              return (
                <ThemedToolTip
                  key={m.id}
                  text={
                    multiSelect
                      ? isSelected
                        ? `Deselect "${m.title}"`
                        : `Select "${m.title}"`
                      : `Add ${selectedCount} ${itemTypeLabel} to "${m.title}"`
                  }
                >
                  <TouchableOpacity
                    style={[
                      styles.memoryRow,
                      {
                        backgroundColor: isSelected
                          ? selectorTheme.row.selectedBg
                          : isHovered
                          ? selectorTheme.row.hoverBg
                          : selectorTheme.row.restingBg,
                        borderColor: isSelected
                          ? tintColor
                          : isHovered
                          ? hoverBorderColor
                          : borderColor,
                      },
                    ]}
                    onPress={() => handleSelect(m)}
                    disabled={!!submittingId}
                    activeOpacity={0.7}
                    {...(Platform.OS === "web"
                      ? {
                          onMouseEnter: () => setHoveredMemoryId(m.id || null),
                          onMouseLeave: () => setHoveredMemoryId(null),
                        }
                      : {})}
                  >
                    <View style={styles.memoryRowLeft}>
                      <Ionicons
                        name="book-outline"
                        size={16}
                        color={tintColor}
                        style={styles.rowIcon}
                      />
                      <View style={styles.rowTextContainer}>
                        <ThemedText
                          type="defaultSemiBold"
                          style={styles.rowTitle}
                          numberOfLines={1}
                        >
                          {m.title}
                        </ThemedText>
                        <View style={styles.metaRow}>
                          {formattedDate ? (
                            <ThemedText style={[styles.metaText, { color: textMuted }]}>
                              {formattedDate}
                            </ThemedText>
                          ) : null}
                          <ThemedText style={[styles.metaText, { color: textMuted }]}>
                            • {mediaCount} media
                          </ThemedText>
                          {formatLocation(m.location) ? (
                            <ThemedText
                              style={[styles.metaText, { color: textMuted }]}
                              numberOfLines={1}
                            >
                              • 📍 {formatLocation(m.location)}
                            </ThemedText>
                          ) : null}
                        </View>
                      </View>
                    </View>

                    {isSubmitting ? (
                      <ActivityIndicator size="small" color={tintColor} />
                    ) : multiSelect ? (
                      <Ionicons
                        name={isSelected ? "checkmark-circle" : "ellipse-outline"}
                        size={20}
                        color={isSelected ? tintColor : textMuted}
                      />
                    ) : (
                      <Ionicons name="arrow-forward-outline" size={16} color={tintColor} />
                    )}
                  </TouchableOpacity>
                </ThemedToolTip>
              );
            })
          )}
        </ScrollView>
      </View>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  searchWrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchIcon: {
    position: "absolute",
    left: 20,
    zIndex: 2,
  },
  searchContainer: {
    flex: 1,
    marginVertical: 0,
  },
  searchInput: {
    paddingLeft: 28,
    height: 32,
    fontSize: 12,
    borderRadius: 4,
  },
  clearSearchBtn: {
    position: "absolute",
    right: 18,
    zIndex: 2,
  },
  createNewRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginHorizontal: 8,
    marginBottom: 4,
    padding: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "dashed",
    gap: 6,
  },
  createNewIconCircle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  createNewTextCol: {
    flex: 1,
  },
  createNewTitle: {
    fontSize: 11,
  },
  createNewSubtitle: {
    fontSize: 9,
    marginTop: 1,
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 8,
    paddingBottom: 4,
    gap: 4,
  },
  memoryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  memoryRowLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 6,
    gap: 6,
  },
  rowIcon: {
    opacity: 0.8,
  },
  rowTextContainer: {
    flex: 1,
  },
  rowTitle: {
    fontSize: 11,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 3,
    marginTop: 1,
  },
  metaText: {
    fontSize: 9,
  },
  centerContainer: {
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  infoText: {
    fontSize: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 6,
    borderTopWidth: 1,
  },
  cancelBtn: {
    minWidth: 64,
  },
  doneBtn: {
    minWidth: 70,
  },
});

export const ThemedMemorySelector = MemorySelector;
export const AddToMemoryModal = MemorySelector;


